import React, { useEffect, useState } from 'react';

const NOTIFICATION_FIELDS = [
  { key: 'temperature', title: 'Temperatura', icon: 'T', description: 'Aplica a todas las alertas de temperatura.' },
  { key: 'oxygen', title: 'Oxigeno Disuelto', icon: 'O2', description: 'Aplica a todas las alertas de oxigeno disuelto.' },
  { key: 'saturation', title: 'Saturacion', icon: 'Sat', description: 'Aplica a todas las alertas de saturacion.' },
];

const formatInterval = (seconds) => {
  if (seconds < 60) return `${seconds}s`;
  if (seconds % 60 === 0) return `${seconds / 60} min`;
  return `${Math.floor(seconds / 60)}m ${seconds % 60}s`;
};

export const NotificationConfigPanel = ({ isOpen, onClose, config, onSave }) => {
  const [localConfig, setLocalConfig] = useState(config || {});
  const [applyAllValue, setApplyAllValue] = useState('');
  const [errors, setErrors] = useState({});

  useEffect(() => {
    setLocalConfig(config || {});
    setApplyAllValue('');
    setErrors({});
  }, [config]);

  const handleChange = (key, value) => {
    const numericValue = Number(value);
    setLocalConfig((prev) => ({
      ...prev,
      notificationTimings: {
        ...prev.notificationTimings,
        [key]: numericValue,
      },
    }));

    setErrors((prev) => ({
      ...prev,
      [key]: !Number.isFinite(numericValue) || numericValue < 30 || numericValue > 86400
        ? 'Debe estar entre 30 y 86400 segundos'
        : null,
    }));
  };

  const hasErrors = Object.values(errors).some(Boolean);

  const handleApplyAll = () => {
    const numericValue = Number(applyAllValue);
    const error = !Number.isFinite(numericValue) || numericValue < 30 || numericValue > 86400
      ? 'Debe estar entre 30 y 86400 segundos'
      : null;

    if (error) {
      setErrors((prev) => ({
        ...prev,
        applyAll: error,
      }));
      return;
    }

      const nextConfig = NOTIFICATION_FIELDS.reduce((acc, field) => {
      acc[field.key] = numericValue;
      return acc;
    }, {});

    setLocalConfig((prev) => ({
      ...prev,
      notificationTimings: { ...prev.notificationTimings, ...nextConfig },
    }));
    setErrors({});
  };

  const handleLevelToggle = (levelKey) => {
    setLocalConfig((prev) => ({
      ...prev,
      notificationLevels: {
        ...prev.notificationLevels,
        [levelKey]: !prev.notificationLevels?.[levelKey],
      },
    }));
  };

  const handleSave = () => {
    if (hasErrors) return;
    onSave(localConfig);
    onClose();
  };

  return (
    <>
      <div className={`sidebar-overlay ${isOpen ? 'active' : ''}`} onClick={onClose} />
      <div className={`config-panel ${isOpen ? 'active' : ''}`}>
        <div className="config-header">
          <div className="config-title">
            <span>Al</span>
            Frecuencia de Notificaciones
          </div>
          <button className="sidebar-close" onClick={onClose}>x</button>
        </div>

        <div className="config-content">
          <div className="history-info-card">
            <div className="history-info-icon">i</div>
            <div className="history-info-text">
              Define cada cuantos segundos puede volver a enviarse una alerta del mismo parametro en la misma piscina.
            </div>
          </div>

          <div className="config-card">
            <div className="config-card-header">
              <span className="config-card-icon">Lvl</span>
              <span className="config-card-title">Tipos de Alerta</span>
            </div>

            <div className="notification-level-list">
              <button
                type="button"
                className={`notification-level-toggle ${localConfig.notificationLevels?.warning ? 'active warning' : ''}`}
                onClick={() => handleLevelToggle('warning')}
              >
                <span className="notification-level-toggle-title">Advertencias</span>
                <span className="notification-level-toggle-text">
                  {localConfig.notificationLevels?.warning ? 'Activas' : 'Desactivadas'}
                </span>
              </button>

              <button
                type="button"
                className={`notification-level-toggle ${localConfig.notificationLevels?.critical ? 'active critical' : ''}`}
                onClick={() => handleLevelToggle('critical')}
              >
                <span className="notification-level-toggle-title">Criticas</span>
                <span className="notification-level-toggle-text">
                  {localConfig.notificationLevels?.critical ? 'Activas' : 'Desactivadas'}
                </span>
              </button>
            </div>
          </div>

          <div className="config-card">
            <div className="config-card-header">
              <span className="config-card-icon">All</span>
              <span className="config-card-title">Aplicar a Todos</span>
            </div>

            <div className="notification-config-item notification-config-item-single">
              <div className="notification-config-meta">
                <div className="notification-config-name">Mismo tiempo para todos los parametros</div>
                <div className="notification-config-current">
                  Sobrescribe temperatura, oxigeno y saturacion con un solo valor.
                </div>
              </div>

              <div className="notification-apply-all-controls">
                <div className="config-field">
                  <label className="config-label">Segundos de espera</label>
                  <input
                    type="number"
                    min="30"
                    max="86400"
                    step="30"
                    className={`config-input ${errors.applyAll ? 'error' : ''}`}
                    value={applyAllValue}
                    onChange={(e) => setApplyAllValue(e.target.value)}
                  />
                  {errors.applyAll && <div className="config-error">{errors.applyAll}</div>}
                </div>
                <button type="button" className="config-save-btn notification-apply-all-btn" onClick={handleApplyAll}>
                  Aplicar
                </button>
              </div>
            </div>
          </div>

          <div className="config-card">
            <div className="config-card-header">
              <span className="config-card-icon">Cfg</span>
              <span className="config-card-title">Tiempos por Parametro</span>
            </div>

            <div className="notification-config-list">
              {NOTIFICATION_FIELDS.map((group) => (
                <div key={group.key} className="notification-config-item">
                  <div className="notification-config-meta">
                    <div className="notification-config-name">{group.title}</div>
                    <div className="notification-config-current">
                        Actual: {formatInterval(Number(localConfig.notificationTimings?.[group.key] || 0))}
                    </div>
                    <div className="notification-config-current">{group.description}</div>
                  </div>

                  <div className="config-field">
                    <label className="config-label">Segundos de espera</label>
                    <input
                      type="number"
                      min="30"
                      max="86400"
                      step="30"
                      className={`config-input ${errors[group.key] ? 'error' : ''}`}
                      value={localConfig.notificationTimings?.[group.key] ?? ''}
                      onChange={(e) => handleChange(group.key, e.target.value)}
                    />
                    {errors[group.key] && <div className="config-error">{errors[group.key]}</div>}
                  </div>
                </div>
              ))}
            </div>
          </div>

          <button
            className="config-save-btn"
            onClick={handleSave}
            disabled={hasErrors}
          >
            Guardar Frecuencia de Notificaciones
          </button>
        </div>
      </div>
    </>
  );
};
